import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  Grid,
  Link,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { ColorPallet } from "../../../utils/colorPallet";
import {
  RestockProductBatchHeaders,
  RestockProductHeader,
} from "../../../utils/constants";
import { useAppDispatch } from "../../../app/hooks";
import { getAllMedBatch } from "../../../features/batch/batchSlice";
import RestorIcon from "../../IconComp/restorIcon";
import ReminderModal from "./reminder-model";

interface IProps {
  selectedMedicineId: any;
  setOpenRestockModal?: any;
  setOpenReminder?: any;
  openReminder?: any;
  setSelectedBatch?: any;
}

const ProductHistoryModal = ({
  setSelectedBatch,
  selectedMedicineId,
  setOpenRestockModal,
  setOpenReminder,
  openReminder,
}: IProps) => {
  const dispatch = useAppDispatch();
  const pharmacyId = localStorage?.getItem("pharmacyId");
  const [restockBatchData, setRestockBatchData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  //const [openReminder, setOpenReminder] = useState(false);

  const getRestockBatchData = useCallback(async () => {
    const dto = {
      medicine_id: selectedMedicineId,
      pharmacy_id: pharmacyId,
    };

    try {
      setIsLoading(true);
      const res = await dispatch(getAllMedBatch(dto));
      if (res?.payload?.success) {
        setRestockBatchData(res.payload.data); // <-- store the result in state
      }
    } catch (error) {
      console.error("Error fetching batch data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, pharmacyId, selectedMedicineId]);

  useEffect(() => {
    getRestockBatchData();
  }, [getRestockBatchData]);

  return (
    <>
      <Box p={2}>
        {/* Top Medicine Info */}
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {RestockProductHeader?.map((item, index) => (
                <TableCell
                  sx={{
                    border: `1px solid ${ColorPallet?.borderColor}`,
                    paddingY: "2px !important",
                    paddingLeft: "8px !important",
                  }}
                  key={index}
                >
                  {item.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {restockBatchData?.[0]?.length > 0 && (
              <TableRow>
                <TableCell
                  sx={{
                    border: `1px solid ${ColorPallet?.borderColor}`,
                    paddingY: "4px",
                    paddingX: "8px",
                  }}
                >
                  {restockBatchData[0][0]?.pharmacy_medicine?.name || "-"}
                </TableCell>
                <TableCell
                  sx={{
                    border: `1px solid ${ColorPallet?.borderColor}`,
                    paddingY: "4px",
                    paddingX: "8px",
                  }}
                >
                  {restockBatchData[0].reduce(
                    (sum: number, item: any) => sum + (item.quantity || 0),
                    0
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    border: `1px solid ${ColorPallet?.borderColor}`,
                    paddingY: "4px",
                    paddingX: "8px",
                  }}
                >
                  {restockBatchData[0].reduce(
                    (remaining: number, item: any) => {
                      const quantity = item.quantity || 0;
                      const sold = item.quantity_sold || 0;
                      return remaining + (quantity - sold);
                    },
                    0
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Batches List Title */}
        <Box sx={{ fontWeight: "700", mb: 1, fontSize: "16px", mt: 1 }}>
          Batches List
        </Box>

        {/* Batches Table */}
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {RestockProductBatchHeaders.map((item, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                      fontWeight: 600,
                    }}
                  >
                    {item.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(restockBatchData?.[0] || [])?.map((item: any, index: any) => (
                <TableRow key={index}>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                      color: "#1976d2",
                      fontWeight: 500,
                    }}
                  >
                    {item.batch_no ? item.batch_no : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.total_box ? item.total_box : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.unit_per_box ? item.unit_per_box : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.quantity ? item.quantity : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.cost_price ? item.cost_price : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.sell_price ? item.sell_price : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.shelf?.name ? item.shelf?.name : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                    }}
                  >
                    {item.expiry_date
                      ? new Date(item.expiry_date).toLocaleDateString("en-GB") // formats as DD/MM/YYYY
                      : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: `1px solid ${ColorPallet?.borderColor}`,
                      paddingY: "4px",
                      paddingX: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <Tooltip title="Restock Batch" arrow>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent bubbling if needed
                          setSelectedBatch(item);
                          setOpenRestockModal(false); // Close previous modal
                          setOpenReminder(true); // Open new modal
                        }}
                        sx={{ display: "inline-flex", alignItems: "center" }}
                      >
                        <RestorIcon />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* <ReminderModal
        open={openReminder}
        onClose={() => setOpenReminder(false)}
        onContinue={() => {
          setOpenReminder(false);
        }}
      /> */}
    </>
  );
};

export default ProductHistoryModal;
